import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react'
import { Button, Card, cn } from '@saas-pages/shared/ui'

interface CodePaneStat {
  label: ReactNode
  value: ReactNode
}

interface CodePane {
  title: ReactNode
  value: string
  ariaLabel: string
  placeholder?: string
  actions?: ReactNode
  clear?: { label: ReactNode; onClick: () => void }
  onChange?: (value: string) => void
  onPaste?: () => void
  readOnly?: boolean
  stats?: CodePaneStat[]
  status?: ReactNode
  minHeightClassName?: string
  textareaClassName?: string
  wrap?: TextareaHTMLAttributes<HTMLTextAreaElement>['wrap']
}

interface CodeWorkspaceProps {
  input: CodePane
  output: CodePane
  controls?: ReactNode
  /** Shown above the panes, so it stays visible on either mobile tab. */
  error?: ReactNode
  notice?: ReactNode
  className?: string
  editorGridClassName?: string
}

function CodeWorkspacePane({
  title,
  value,
  ariaLabel,
  placeholder,
  actions,
  clear,
  onChange,
  onPaste,
  readOnly,
  stats,
  status,
  minHeightClassName,
  textareaClassName,
  wrap = 'off',
}: CodePane) {
  return (
    <Card className="flex min-h-0 flex-col overflow-hidden shadow-sm lg:h-[clamp(17.5rem,calc(100dvh-30rem),32.5rem)]">
      <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
        <div className="text-sm font-medium tracking-normal">{title}</div>
        {(actions || clear) && (
          <div className="flex flex-wrap gap-2">
            {actions}
            {clear && (
              <Button variant="outline" size="sm" onClick={clear.onClick}>
                {clear.label}
              </Button>
            )}
          </div>
        )}
      </div>
      <textarea
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        onPaste={onPaste}
        readOnly={readOnly}
        placeholder={placeholder}
        className={cn(
          'min-w-0 w-full flex-1 resize-y overflow-auto border-0 p-5 lg:resize-none',
          'bg-card font-mono text-[13px] leading-5 text-foreground',
          'placeholder:text-muted-foreground/55',
          'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
          minHeightClassName ?? 'min-h-[280px] lg:min-h-0',
          textareaClassName,
        )}
        aria-label={ariaLabel}
        spellCheck={false}
        wrap={wrap}
      />
      {status}
      {stats && stats.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t bg-muted/25 px-4 py-2 text-xs text-muted-foreground">
          {stats.map((stat, index) => (
            <span key={index}>
              {stat.label}: {stat.value}
            </span>
          ))}
        </div>
      )}
    </Card>
  )
}

export function CodeWorkspace({
  input,
  output,
  controls,
  error,
  notice,
  className,
  editorGridClassName,
}: CodeWorkspaceProps) {
  const [activePane, setActivePane] = useState<'input' | 'output'>('input')
  // Set by a run button or a paste; the next result opens the output tab.
  // Plain typing never does, so live-updating tools do not steal the input tab.
  const awaitingResultRef = useRef(false)
  const pastedRef = useRef(false)
  const inputTabRef = useRef<HTMLButtonElement>(null)
  const outputTabRef = useRef<HTMLButtonElement>(null)
  const id = useId()
  const inputTabId = `${id}-input-tab`
  const outputTabId = `${id}-output-tab`
  const inputPanelId = `${id}-input-panel`
  const outputPanelId = `${id}-output-panel`

  useEffect(() => {
    if (!awaitingResultRef.current || !output.value) return
    awaitingResultRef.current = false
    setActivePane('output')
  }, [output.value])

  useEffect(() => {
    if (error) awaitingResultRef.current = false
  }, [error])

  const handleRunClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!(event.target as Element).closest('[data-code-run]')) return
    if (output.value) {
      setActivePane('output')
    } else {
      awaitingResultRef.current = true
    }
  }

  const handleInputChange = (value: string) => {
    awaitingResultRef.current = pastedRef.current
    pastedRef.current = false
    input.onChange?.(value)
  }

  const selectPane = (pane: 'input' | 'output', focus = false) => {
    setActivePane(pane)
    if (focus) {
      const target = pane === 'input' ? inputTabRef : outputTabRef
      target.current?.focus()
    }
  }

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    let nextPane: 'input' | 'output' | null = null

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'Home') {
      nextPane = 'input'
    } else if (
      event.key === 'ArrowRight'
      || event.key === 'ArrowDown'
      || event.key === 'End'
    ) {
      nextPane = 'output'
    }

    if (!nextPane) return
    event.preventDefault()
    selectPane(nextPane, true)
  }

  return (
    <section className={cn('space-y-4', className)}>
      {controls && (
        <div className="rounded-xl border bg-card/90 p-4 shadow-sm" onClickCapture={handleRunClick}>
          {controls}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}
      {notice && !error && (
        <div
          role="status"
          className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
        >
          {notice}
        </div>
      )}
      <div
        role="tablist"
        aria-label={`${input.ariaLabel} / ${output.ariaLabel}`}
        className="grid grid-cols-2 rounded-xl border bg-muted/35 p-1 lg:hidden"
      >
        <button
          ref={inputTabRef}
          id={inputTabId}
          type="button"
          role="tab"
          aria-selected={activePane === 'input'}
          aria-controls={inputPanelId}
          tabIndex={activePane === 'input' ? 0 : -1}
          onClick={() => selectPane('input')}
          onKeyDown={handleTabKeyDown}
          className={cn(
            'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            activePane === 'input'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {input.title}
        </button>
        <button
          ref={outputTabRef}
          id={outputTabId}
          type="button"
          role="tab"
          aria-selected={activePane === 'output'}
          aria-controls={outputPanelId}
          tabIndex={activePane === 'output' ? 0 : -1}
          onClick={() => selectPane('output')}
          onKeyDown={handleTabKeyDown}
          className={cn(
            'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            activePane === 'output'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {output.title}
          {output.value && activePane === 'input' && (
            <span className="ml-1.5 inline-block size-1.5 rounded-full bg-primary align-middle" aria-hidden />
          )}
        </button>
      </div>
      <div className={cn('grid min-w-0 gap-4 lg:grid-cols-2', editorGridClassName)}>
        <div
          id={inputPanelId}
          role="tabpanel"
          aria-labelledby={inputTabId}
          className={cn('min-w-0', activePane !== 'input' && 'hidden lg:block')}
        >
          <CodeWorkspacePane
            {...input}
            clear={input.value || output.value ? input.clear : undefined}
            onChange={handleInputChange}
            onPaste={() => {
              pastedRef.current = true
            }}
          />
        </div>
        <div
          id={outputPanelId}
          role="tabpanel"
          aria-labelledby={outputTabId}
          className={cn('min-w-0', activePane !== 'output' && 'hidden lg:block')}
        >
          <CodeWorkspacePane {...output} actions={output.value ? output.actions : undefined} />
        </div>
      </div>
    </section>
  )
}
